import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Switch, Form, Space, Typography, message } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as serverconfig from '../../serverconn';

const RIGHTS_FIELDS = [
  { key: 'can_do_supply_pickup', label: 'Supply pickup' },
  { key: 'can_do_sample_pickup', label: 'Sample pickup' },
  { key: 'can_do_other_items_pickup', label: 'Other items pickup' },
  { key: 'can_do_sample_results_pickup', label: 'Sample results pickup' },
  { key: 'can_do_package_pickup', label: 'Package pickup' },
];

function AgentRights({ agentId, agentName }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rightsId, setRightsId] = useState(null);
  const [form] = Form.useForm();

  const title = useMemo(() => {
    if (agentName) return `Agent rights — ${agentName}`;
    return 'Agent rights';
  }, [agentName]);

  const open = () => setVisible(true);
  const close = () => setVisible(false);

  useEffect(() => {
    if (!visible) return;
    if (!agentId) return;

    setLoading(true);
    axios
      .get(`${serverconfig.backendserverurl}/api/delivery_agent_rights/?delivery_agent=${agentId}`)
      .then((res) => {
        const existing = Array.isArray(res.data) ? res.data[0] : null;
        if (existing) {
          setRightsId(existing.id);
          const initialValues = RIGHTS_FIELDS.reduce((acc, f) => {
            acc[f.key] = Boolean(existing[f.key]);
            return acc;
          }, {});
          form.setFieldsValue(initialValues);
        } else {
          setRightsId(null);
          const defaults = RIGHTS_FIELDS.reduce((acc, f) => {
            acc[f.key] = false;
            return acc;
          }, {});
          form.setFieldsValue(defaults);
        }
      })
      .catch((err) => {
        console.error('Failed to load agent rights', err);
        message.error('Failed to load agent rights');
      })
      .finally(() => setLoading(false));
  }, [visible, agentId, form]);

  const handleSave = () => {
    if (!agentId) return;
    const values = form.getFieldsValue();
    const payload = { ...values, delivery_agent: agentId };

    setSaving(true);
    const request = rightsId
      ? axios.put(`${serverconfig.backendserverurl}/api/delivery_agent_rights/${rightsId}/`, payload)
      : axios.post(`${serverconfig.backendserverurl}/api/delivery_agent_rights/`, payload);

    request
      .then((res) => {
        if (!rightsId) setRightsId(res.data?.id ?? null);
        message.success('Agent rights saved');
        close();
      })
      .catch((err) => {
        console.error('Failed to save agent rights', err);
        message.error('Failed to save agent rights');
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Button size="small" icon={<SafetyCertificateOutlined />} onClick={open}>
        Rights
      </Button>

      <Modal
        title={title}
        visible={visible}
        onCancel={close}
        confirmLoading={saving}
        onOk={handleSave}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" disabled={loading}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Toggle the allowed operations for this delivery agent.
            </Typography.Text>
            {RIGHTS_FIELDS.map((field) => (
              <Form.Item key={field.key} label={field.label} name={field.key} valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            ))}
          </Space>
        </Form>
      </Modal>
    </>
  );
}

export default AgentRights;

